/**
 * useDataSource Hook
 *
 * Fetches dropdown options from Firestore based on DataSourceConfig.
 * Handles:
 * - Preset expansion
 * - Collection queries with filters
 * - Linked filters (values from other form fields)
 * - Label template formatting
 * - Caching via React Query
 * - Public form access via HTTP endpoint (bypasses Firestore security rules)
 */

import { useCallback, useMemo, useRef } from 'react';
import { useQuery, type QueryClient } from '@tanstack/react-query';
import {
  collection,
  query,
  where,
  limit,
  orderBy,
  startAfter,
  documentId,
  getDocs,
  QueryConstraint,
  type Query,
  type QueryDocumentSnapshot,
  type DocumentData,
} from 'firebase/firestore';
import { httpsCallable } from '../../../../lib/firebase/authReadyCallable';
import * as Sentry from '@sentry/react';
import { auth, db, functions } from '../../../../config/firebase';
import { isSoftDeleted } from '../../../../lib/appsheet/softDelete';
import { DataSourceConfig, DataSourceOption, DataSourcePresetId, DataSourceResult, LinkedFilter } from './types';
import { expandPreset, COMPUTE_LABEL_FUNCTIONS, DERIVED_PRESETS } from './presets';
import { useOrganization } from '../../../../contexts/OrganizationContext';
import { usePublicFormContext } from '../PublicFormContext';
import { canInflateGzip, inflateGzipBase64 } from './inflateGzipBase64';
import { applyDelta, copyFromFull, sinceOf, validateCopy, type ProjectedCopy, type ProjectedDeltaWire, type ProjectedRow, type ProjectedSnapshotWire } from './projectedCopy';

/** The only client-side linked filter that can be applied to options instead of docs, and its inputs. */
const DEFERRABLE_COMPUTE_FIELD = 'compositeClientName';
const COMPOSITE_INPUT_FIELDS = ['Management Company', 'Client Name', 'Site Name'];

// API base URL for public data source endpoint
const API_BASE_URL = process.env.REACT_APP_FUNCTIONS_URL || 'https://us-central1-quoter-saas.cloudfunctions.net';

/**
 * The React Query key one data-source read lives under. Pure and exported so code outside the hook (the tech app's
 * offline copy of the generator list) addresses the SAME cache entry the hook reads; a test pins the two together.
 * @param {object} args what the key is made of
 * @return {unknown[]} the query key
 */
export function buildDataSourceQueryKey(args: {
  presetId: string | null;
  resolvedConfig: DataSourceConfig | undefined;
  fetchConfig: DataSourceConfig | undefined;
  formValues: Record<string, unknown>;
  organizationId: string | undefined;
  formSlug: string | null;
}): unknown[] {
  const { presetId, resolvedConfig, fetchConfig, formValues, organizationId, formSlug } = args;
  if (!resolvedConfig || !fetchConfig) return ['data-source', 'none'];

  const linkedValues = (fetchConfig.linkedFilters || []).map((f) => formValues[f.sourceField]);
  // Everything that changes the SHAPE of the options is part of the key, so two consumers of one preset with
  // different overrides (limit, includeFields, label, ...) can never read each other's cache entry.
  const shape = JSON.stringify([
    resolvedConfig.limit, resolvedConfig.includeFields, resolvedConfig.labelTemplate, resolvedConfig.labelField,
    resolvedConfig.valueField, resolvedConfig.computeLabel, resolvedConfig.computeLabelAsValue,
    resolvedConfig.deduplicate, resolvedConfig.sortField, resolvedConfig.sortDirection, resolvedConfig.serverFetch,
  ]);

  return [
    'data-source',
    presetId,
    resolvedConfig.collection,
    JSON.stringify(resolvedConfig.filters),
    JSON.stringify(linkedValues),
    shape,
    organizationId,
    // #3880: the server now serves each public form only the fields IT
    // auto-fills, so two public forms sharing a preset get different option
    // data and must not share a cache entry.
    formSlug,
  ];
}

/**
 * The key the authenticated `generators` preset (and, through fix A's derivation, `customers-active` and the FSR
 * equipment dropdown) is cached under for `organizationId`.
 * @param {string} organizationId the organization
 * @return {unknown[]} the query key
 */
export function generatorsDataSourceQueryKey(organizationId: string): unknown[] {
  return presetDataSourceQueryKey('generators', organizationId);
}

/**
 * The key the authenticated `parts-catalog` preset (the FSR parts grid and the ticket's part-number lookup, no overrides) is
 * cached under for `organizationId`; the technician app's offline copy of the parts list addresses it (pinned in the queryKey test).
 * @param {string} organizationId the organization
 * @return {unknown[]} the query key
 */
export function partsCatalogDataSourceQueryKey(organizationId: string): unknown[] {
  return presetDataSourceQueryKey('parts-catalog', organizationId);
}

/**
 * The key a plain preset read (no overrides, no linked-filter values) lives under for `organizationId`.
 * @param {DataSourcePresetId} presetId the preset
 * @param {string} organizationId the organization
 * @return {unknown[]} the query key
 */
export function presetDataSourceQueryKey(presetId: DataSourcePresetId, organizationId: string): unknown[] {
  const resolved = resolvedPresetConfig(presetId);
  return buildDataSourceQueryKey({
    presetId,
    resolvedConfig: resolved,
    fetchConfig: resolved,
    formValues: {},
    organizationId,
    formSlug: null,
  });
}

/** A plain preset exactly as the hook resolves it (no overrides, no linked filters). */
function resolvedPresetConfig(presetId: DataSourcePresetId): DataSourceConfig | undefined {
  const expanded = expandPreset(presetId);
  return expanded && {
    ...expanded,
    type: 'collection',
    filters: [...(expanded.filters || [])],
    linkedFilters: [...(expanded.linkedFilters || [])],
  };
}

/** How long a data-source read is fresh / kept. ONE definition for the hook and for prefetching, so a prefetch can never go stale sooner or later than the dropdown's own read. */
export const DATA_SOURCE_STALE_MS = 1000 * 60 * 5;
export const DATA_SOURCE_GC_MS = 1000 * 60 * 30;

/**
 * Start reading the FSR generators list into the cache the dropdowns read, so the form opens with it already there.
 * Same key and same read as the dropdown's own query: React Query shares an in-flight read and skips one whose data is
 * still fresh, so the dropdown never makes a second request. Resolves when the read ends; never rejects (a failed
 * prefetch just leaves the dropdown to read for itself).
 * @param {QueryClient} queryClient the app's client
 * @param {string} organizationId the organization
 * @return {Promise<void>} settles when the prefetch is over
 */
export function prefetchGeneratorsDataSource(queryClient: QueryClient, organizationId: string): Promise<void> {
  const resolved = resolvedPresetConfig('generators');
  if (!resolved) return Promise.resolve();
  return queryClient.prefetchQuery({
    queryKey: generatorsDataSourceQueryKey(organizationId),
    queryFn: () => fetchDataSourceOptions(resolved, {}, organizationId),
    staleTime: DATA_SOURCE_STALE_MS,
    gcTime: DATA_SOURCE_GC_MS,
  });
}

// ============================================================================
// Main Hook
// ============================================================================

interface UseDataSourceOptions {
  /** The data source configuration */
  config: DataSourceConfig | undefined;
  /** Current form values (for linked filters) */
  formValues?: Record<string, unknown>;
  /** Whether to skip fetching (e.g., if parent field is empty) */
  skip?: boolean;
  /**
   * Opt in to sharing ONE read across changes of a client-side linked-filter source (the FSR `equipmentId`
   * dropdown filtered by `customer`): the filter is applied to the options after the read instead of being
   * part of the read's cache key. Opt-in because a consumer such as DataSourceCombobox relies on the options
   * going empty on a source change to clear its own value (it does not take this path). Authenticated
   * direct-Firestore path only.
   */
  shareClientFilterRead?: boolean;
}

export function useDataSource({
  config: configProp,
  formValues = {},
  skip = false,
  shareClientFilterRead = false,
}: UseDataSourceOptions): DataSourceResult {
  // Try to get organizationId from auth context first, then fall back to public form context
  const { currentOrganization } = useOrganization();
  const publicFormContext = usePublicFormContext();
  const organizationId = currentOrganization?.organizationId || publicFormContext.organizationId;

  // Determine if we're in a public (unauthenticated) context
  // Public forms have no currentOrganization but do have publicFormContext.organizationId
  const isPublicContext = !currentOrganization && !!publicFormContext.organizationId;


  // A caller may pass a fresh config object every render; everything below is keyed on its CONTENT so the
  // memos (and the select they feed) stay stable.
  const configContentKey = JSON.stringify(configProp ?? null);
  const stableConfigRef = useRef<{ key: string; value: DataSourceConfig | undefined }>({ key: configContentKey, value: configProp });
  if (stableConfigRef.current.key !== configContentKey) stableConfigRef.current = { key: configContentKey, value: configProp };
  const requestedConfig = stableConfigRef.current.value;

  // A derived preset (customers-active) reads through its source preset's query so the two share ONE
  // collection read (see DERIVED_PRESETS). Authenticated direct-Firestore path only; a plain preset only.
  const derived = useMemo(() => {
    if (isPublicContext || requestedConfig?.type !== 'preset' || !requestedConfig.preset) return undefined;
    const spec = DERIVED_PRESETS[requestedConfig.preset];
    const plain = Object.keys(requestedConfig).every((k) => k === 'type' || k === 'preset');
    return spec && plain ? spec : undefined;
  }, [isPublicContext, requestedConfig]);
  const config = useMemo<DataSourceConfig | undefined>(
    () => (derived ? { type: 'preset', preset: derived.from } : requestedConfig),
    [derived, requestedConfig]
  );

  // Expand preset to full config if needed
  const resolvedConfig = useMemo(() => {
    if (!config) return undefined;

    if (config.type === 'preset' && config.preset) {
      const expanded = expandPreset(config.preset);
      if (!expanded) {
        console.warn(`Unknown data source preset: ${config.preset}`);
        return undefined;
      }
      // Merge any overrides from the original config
      return {
        ...expanded,
        ...config,
        type: 'collection' as const,
        filters: [...(expanded.filters || []), ...(config.filters || [])],
        linkedFilters: [...(expanded.linkedFilters || []), ...(config.linkedFilters || [])],
      };
    }

    return config;
  }, [config]);

  // Check if all linked filter requirements are met
  // Filters with computeField are applied client-side and don't block the query
  const linkedFiltersReady = useMemo(() => {
    if (!resolvedConfig?.linkedFilters?.length) return true;

    return resolvedConfig.linkedFilters.every((filter) => {
      // Client-side computed filters don't block the Firestore query
      if (filter.computeField) return true;
      const sourceValue = formValues[filter.sourceField];
      // Filter is ready if source has a value, OR if clearWhenEmpty is true and we'll handle it
      return sourceValue !== undefined && sourceValue !== null && sourceValue !== '';
    });
  }, [resolvedConfig?.linkedFilters, formValues]);

  // Should we clear because a required linked filter is empty?
  const shouldClear = useMemo(() => {
    if (!resolvedConfig?.linkedFilters?.length) return false;

    return resolvedConfig.linkedFilters.some((filter) => {
      if (!filter.clearWhenEmpty) return false;
      const sourceValue = formValues[filter.sourceField];
      return sourceValue === undefined || sourceValue === null || sourceValue === '';
    });
  }, [resolvedConfig?.linkedFilters, formValues]);

  // Client-side `compositeClientName` linked filters (the FSR `equipmentId` field, filtered by `customer`) do not
  // change WHAT is read from Firestore, only which rows are kept. Keying the query on the customer re-downloaded
  // the whole generators collection (10.7 MB) on every customer change (measured 2026-10-08). So for these, the
  // read is keyed WITHOUT the customer and the filter runs on the options afterwards (`select`). Only when that is
  // exact: the compute function's inputs are in the options' data, and no de-dup/limit sits between read and filter.
  const deferral = useMemo(() => {
    const clientSide = (resolvedConfig?.linkedFilters ?? []).filter((f) => f.computeField);
    if (!shareClientFilterRead || isPublicContext || !resolvedConfig || clientSide.length === 0) return undefined;
    // serverFetch and public reads never applied linkedFilters (only fetchDataSourceOptions does): keep that.
    const exact =
      !resolvedConfig.serverFetch &&
      clientSide.every((f) => f.computeField === DEFERRABLE_COMPUTE_FIELD) &&
      !resolvedConfig.deduplicate &&
      !resolvedConfig.limit &&
      COMPOSITE_INPUT_FIELDS.every((x) => resolvedConfig.includeFields?.includes(x));
    return exact ? clientSide : undefined;
  }, [resolvedConfig, shareClientFilterRead, isPublicContext]);
  const fetchConfig = useMemo(
    () =>
      resolvedConfig && deferral
        ? { ...resolvedConfig, linkedFilters: (resolvedConfig.linkedFilters ?? []).filter((f) => !deferral.includes(f)) }
        : resolvedConfig,
    [resolvedConfig, deferral]
  );
  const deferredValuesKey = JSON.stringify((deferral ?? []).map((f) => formValues[f.sourceField] ?? null));
  const select = useCallback(
    (opts: DataSourceOption[]): DataSourceOption[] => {
      let out = opts;
      const values: unknown[] = JSON.parse(deferredValuesKey);
      (deferral ?? []).forEach((f, i) => {
        const v = values[i];
        if (v === undefined || v === null || v === '') return; // same as the fetch-time rule: an empty source keeps every row
        const fn = COMPUTE_LABEL_FUNCTIONS[f.computeField!];
        if (!fn) return;
        out = out.filter((o) => fn(o.data ?? {}) === String(v));
      });
      return derived ? derived.derive(out) : out;
    },
    [deferral, deferredValuesKey, derived]
  );

  // Build query key including linked filter values
  // IMPORTANT: Include preset name to prevent cache collisions between presets
  // that query the same collection with the same filters but format data differently
  // (e.g., service-logs vs technicians vs customers-active all query appsheet_service_logs)
  const queryKey = useMemo(
    () =>
      buildDataSourceQueryKey({
        presetId: config?.type === 'preset' ? config.preset ?? null : null,
        resolvedConfig,
        fetchConfig,
        formValues,
        organizationId,
        formSlug: isPublicContext ? publicFormContext.formSlug ?? null : null,
      }),
    [resolvedConfig, fetchConfig, config, formValues, organizationId, isPublicContext, publicFormContext.formSlug]
  );

  // Fetch data
  const {
    data: options = [],
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey,
    queryFn: async () => {
      if (!resolvedConfig || !resolvedConfig.collection) {
        return [];
      }

      // For authenticated users, use direct Firestore queries (they have access)
      if (!isPublicContext) {
        // serverFetch presets (parts-catalog) read through the authenticated
        // callable — their backing collection's rules deny client `list`
        // reads, and the server projects away fields the browser must not
        // receive (costs). See DataSourceConfig.serverFetch.
        if (resolvedConfig.serverFetch && config?.type === 'preset' && config.preset) {
          return fetchServerFetchPresetOptions(config.preset, resolvedConfig, formValues, organizationId);
        }
        return fetchDataSourceOptions(fetchConfig ?? resolvedConfig, formValues, organizationId);
      }

      // For public/unauthenticated forms with presets, use HTTP endpoint
      // This bypasses Firestore security rules via admin credentials
      if (config?.type === 'preset' && config.preset && organizationId) {
        // #3863: the slug is the authority now — the server derives the org from
        // the form it resolves, and refuses a preset the form does not declare.
        return fetchPublicDataSourceOptions(config.preset, publicFormContext.formSlug);
      }

      // Public context without preset - can't query Firestore directly
      console.warn('[useDataSource] Public context without preset, cannot fetch data');
      return [];
    },
    // Identity changes only when the deferred customer value (or the derivation) changes, so it re-runs on
    // those, never on every render and never as a network read.
    select: deferral || derived ? select : undefined,
    enabled: !skip && !shouldClear && !!resolvedConfig && linkedFiltersReady,
    staleTime: DATA_SOURCE_STALE_MS,
    gcTime: DATA_SOURCE_GC_MS,
  });

  // If we should clear, return empty options
  if (shouldClear) {
    return {
      options: [],
      isLoading: false,
      error: null,
      refetch: () => refetch(),
    };
  }

  return {
    // A skipped consumer reads nothing; a derived one must not surface another consumer's cached entry as its own.
    options: derived && skip ? [] : options,
    isLoading,
    error: error as Error | null,
    refetch: () => refetch(),
  };
}

// ============================================================================
// Public Data Source (via HTTP endpoint)
// ============================================================================

/**
 * Fetch data source options via HTTP endpoint for public forms.
 * This bypasses Firestore security rules since the endpoint uses admin credentials.
 */
async function fetchPublicDataSourceOptions(
  preset: string,
  formSlug: string | undefined
): Promise<DataSourceOption[]> {
  // #3863: organizationId is deliberately NOT sent. The server ignores a
  // caller-supplied org and derives it from the resolved public form instead,
  // so sending one would only imply an authority the client does not have.
  if (!formSlug) {
    console.warn('[PublicDataSource] No formSlug in public context — cannot fetch preset options');
    return [];
  }

  const url = `${API_BASE_URL}/getPublicDataSourceOptions?preset=${encodeURIComponent(preset)}&formSlug=${encodeURIComponent(formSlug)}`;

  console.log(`[PublicDataSource] Fetching ${preset} for form ${formSlug}`);

  const response = await fetch(url);

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Failed to fetch options' }));
    throw new Error(error.error || `HTTP ${response.status}`);
  }

  const data = await response.json();

  if (!data.success) {
    throw new Error(data.error || 'Failed to fetch options');
  }

  console.log(`[PublicDataSource] Got ${data.options?.length || 0} options`);

  return data.options || [];
}

// ============================================================================
// Authenticated Server Fetch (serverFetch presets)
// ============================================================================

/**
 * Fetch options through the authenticated forms_getDataSourceOptions callable.
 * The preset id doubles as the server registry's sourceId. A failure throws so
 * React Query surfaces it as `error` — a failed catalog fetch must render as
 * an error state, never as an empty catalog.
 */
async function fetchAuthedPresetOptions(
  preset: string,
  organizationId: string | undefined
): Promise<DataSourceOption[]> {
  if (!organizationId) {
    throw new Error(`serverFetch preset "${preset}" requires an organization context`);
  }
  const getOptions = httpsCallable<
    { sourceId: string; organizationId: string },
    { options: DataSourceOption[] }
  >(functions, 'forms_getDataSourceOptions');
  const result = await getOptions({ sourceId: preset, organizationId });
  return result.data.options || [];
}

/**
 * A serverFetch preset's options. With `serverDocs` set too (parts-catalog, PR5b) and a browser that can inflate gzip, the SAME callable's
 * PROJECTED answer is read through fetchServerDocs (the device copy, `since`, the delta: ~3 KB instead of ~640 KB on a phone that holds the
 * list) and the options are built with the mapping a direct read uses. An answer that is not a projected one (an older server, a gzip that
 * cannot be inflated, a column the mapping needs missing) falls back to the legacy options answer of the same callable. A callable FAILURE
 * (an error with a code: refused, offline, timed out) is not retried as a legacy read: it throws, as before, so the dropdown shows its
 * error state and never an empty catalogue. Never a client-direct Firestore read: the server projects costs and document ids away.
 * @param {string} preset the preset id (= the server registry's sourceId)
 * @param {DataSourceConfig} config the resolved config
 * @param {Record<string, unknown>} formValues the form's values (client-side linked filters)
 * @param {string | undefined} organizationId the caller's organization
 * @return {Promise<DataSourceOption[]>} the options
 */
async function fetchServerFetchPresetOptions(
  preset: string,
  config: DataSourceConfig,
  formValues: Record<string, unknown>,
  organizationId: string | undefined
): Promise<DataSourceOption[]> {
  // Without gzip the server cannot be asked for the projected shape (its contract: acceptGzip or since), so such a browser reads as before.
  if (!config.serverDocs || !organizationId || !canInflateGzip()) return fetchAuthedPresetOptions(preset, organizationId);
  let docs: QueryDocumentSnapshot<DocumentData>[];
  try {
    docs = await fetchServerDocs(config.serverDocs, organizationId, fieldsNeededFromDocs(config));
  } catch (error) {
    // An older server answered the projected request with the legacy options (no second download of the same list).
    if (error instanceof LegacyAnswerError) return error.options;
    if (isCallableFailure(error)) throw error;
    return fetchAuthedPresetOptions(preset, organizationId);
  }
  // Outside the try: a defect in the mapping must surface as an error, not hide as a legacy fallback.
  return mapDocsToOptions(config, docs, formValues).map(withoutRowKey);
}

/**
 * The option exactly as the legacy answer shaped it: the row key (a part number, or part number + U+0001 + index for a twin) is the
 * mapping's `data.id`, which no parts consumer reads and which must not travel into form values or autofill.
 * @param {DataSourceOption} option an option built from projected rows
 * @return {DataSourceOption} the option without `data.id`
 */
function withoutRowKey(option: DataSourceOption): DataSourceOption {
  if (!option.data || !('id' in option.data)) return option;
  const { id: _rowKey, ...data } = option.data;
  return { ...option, data };
}

/** The server answered a projected request with the legacy `options` list (an older server): the caller may use it as is. */
export class LegacyAnswerError extends Error {
  constructor(readonly options: DataSourceOption[]) {
    super('forms_getDataSourceOptions answered with legacy options, not projected documents');
    this.name = 'LegacyAnswerError';
  }
}

/** A failure of the call itself (FirebaseError carries a `code`), as opposed to an answer of the wrong shape (a plain Error from fetchServerDocs). */
const isCallableFailure = (error: unknown): boolean => typeof (error as { code?: unknown } | null)?.code === 'string';

/** What forms_getDataSourceOptions returns for a projected-documents source (functions/src/forms/types.ts ProjectedDocs). */
interface ProjectedDocsResponse {
  options?: DataSourceOption[];
  projectedDocs?: { columns: string[]; rows: Array<Array<string | number | boolean | null>> };
  /** The same projectedDocs as base64 of its gzip; sent INSTEAD of projectedDocs, and only to a caller that asked (acceptGzip). */
  projectedDocsGz?: string;
  /** On a full answer: where the list stands, to send back as `since`. Absent from an older server. */
  snapshot?: ProjectedSnapshotWire;
  /** INSTEAD of the list, when the request carried `since` and little had changed. */
  projectedDelta?: ProjectedDeltaWire;
}

/**
 * Where the phone keeps its copy of a projected list between app opens (FIELD-PERF-2 phase B). The main app keeps it in module memory
 * only; the technician app registers a store backed by its IndexedDB side copies (client-tech/src/offline). A store is advisory: a load
 * that fails, is slow or finds nothing just means the next read asks for the full list. It must scope by signed-in user itself.
 */
export interface ServerDocsCopyStore {
  load(key: string): Promise<ProjectedCopy | undefined>;
  save(key: string, copy: ProjectedCopy): Promise<void>;
  clear(key: string): Promise<void>;
}
let serverDocsCopyStore: ServerDocsCopyStore | null = null;
/** Register (or, with null, remove) the persistent store for copies. */
export function setServerDocsCopyStore(store: ServerDocsCopyStore | null): void {
  serverDocsCopyStore = store;
}
/** How long a read waits for the persistent store before asking the server for the full list without it. */
export const SERVER_DOCS_COPY_LOAD_MS = 800;
/** In-memory copies by `${sourceId}|${organizationId}`: the base for the next read in this app session. */
const serverDocsCopies = new Map<string, ProjectedCopy>();
/** Memory copies are the signed-in user's: keyed by uid too, and another user's are dropped on the next read (customer rows must not outlive a sign-out in memory). */
const currentUid = (): string => auth?.currentUser?.uid ?? '';
const copyKey = (sourceId: string, organizationId: string): string => `${sourceId}|${organizationId}`;
const memoryKey = (key: string, uid: string): string => `${key}|${uid}`;
function dropCopiesOfOtherUsers(uid: string): void {
  Array.from(serverDocsCopies.keys()).filter((k) => !k.endsWith(`|${uid}`)).forEach((k) => serverDocsCopies.delete(k));
}
/** Forget every copy held in memory (sign-out, user switch). The persistent store is wiped by its own owner. */
export function clearServerDocsCopies(): void {
  serverDocsCopies.clear();
}
/** Store writes run one after another, so a clear can never land after the save that replaced it. */
let storeOps: Promise<void> = Promise.resolve();
const enqueueStoreOp = (op: () => Promise<void>): void => {
  storeOps = storeOps.then(op).catch(() => undefined);
};

/** A copy that lacks a column the mapping needs (the preset grew a field since it was saved) cannot be a base. */
const usableCopy = (copy: ProjectedCopy | undefined, required: readonly string[]): ProjectedCopy | undefined =>
  copy && required.every((f) => copy.columns.includes(f)) ? copy : undefined;

/** What the persistent store holds (unchecked), or undefined if it has nothing, fails, or is slower than SERVER_DOCS_COPY_LOAD_MS. Never throws. */
async function loadStoredServerDocsCopy(store: ServerDocsCopyStore, key: string): Promise<unknown> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    return await (Promise.race([
      store.load(key),
      new Promise<undefined>((resolve) => { timer = setTimeout(() => resolve(undefined), SERVER_DOCS_COPY_LOAD_MS); }),
    ]));
  } catch {
    return undefined;
  } finally {
    if (timer !== undefined) clearTimeout(timer);
  }
}

function rememberServerDocsCopy(key: string, uid: string, copy: ProjectedCopy | undefined): void {
  // Never for a read with no uid, nor when the signed-in user changed while the call was in flight: the answer was theirs, not the new user's.
  if (uid === '' || currentUid() !== uid) return;
  const store = serverDocsCopyStore;
  // The store op runs later, possibly after a user switch: it runs only if the same user is still signed in (a save would land under the new user's key).
  const stillHim = (op: (s: ServerDocsCopyStore) => Promise<void>) => (): Promise<void> => (store && currentUid() === uid ? op(store) : Promise.resolve());
  if (!copy) {
    serverDocsCopies.delete(memoryKey(key, uid));
    if (store) enqueueStoreOp(stillHim((s) => s.clear(key)));
    return;
  }
  serverDocsCopies.set(memoryKey(key, uid), copy);
  if (store) enqueueStoreOp(stillHim((s) => s.save(key, copy)));
}

/**
 * How long the server read may take before the client gives up on it and reads directly: the callable's own timeout AND
 * a bound the client enforces itself (so a call the SDK never settles cannot hold the dropdown).
 *
 * 45 s, measured 2026-10-29 UTC (FIELD-PERF, Sentry OFFICEHUB-TECHNICIAN-C / CLIENT-7A): over 14 days the server answered all
 * 1,310 generators calls with p50 2.0 s / p95 3.1 s (1 call >= 10 s), yet phones fell back 84 times. Matched against the
 * server's request log, 30 of those requests had been ANSWERED by the server within 8 s of the read starting (the
 * ~290 KB download was still on its way), 33 only REACHED the server 5 to 39 s after the read started (a weak link
 * sending), and 21 never reached it within 40 s. The old 15 s timeout threw away every answer still coming after 15 s,
 * and the old 10 s race started the 10.7 MB direct read on that same weak link, which cannot win where 290 KB had not
 * arrived. 45 s covers the latest measured arrival (38.6 s) plus the server's p95.
 */
export const SERVER_DOCS_TIMEOUT_MS = 45_000;

/**
 * A transient failure is retried only if it came back within this long of the read starting: a cold or restarting
 * instance answers unavailable / deadline-exceeded at once and then serves. A failure that took longer (a weak link, the
 * client's own timeout) is not retried, because a retry restarts the download from nothing.
 */
export const SERVER_DOCS_RETRY_WINDOW_MS = 10_000;

/** A server answer that took at least this long is reported (once per page load) with how long it took. */
export const SERVER_DOCS_SLOW_REPORT_MS = 10_000;

let serverDocsBudget = { timeoutMs: SERVER_DOCS_TIMEOUT_MS, retryWindowMs: SERVER_DOCS_RETRY_WINDOW_MS, slowReportMs: SERVER_DOCS_SLOW_REPORT_MS };
/** Test hook: shorten parts of the budget (null restores the defaults). */
export function __setServerDocsBudgetForTests(budget: Partial<typeof serverDocsBudget> | null): void {
  serverDocsBudget = { timeoutMs: SERVER_DOCS_TIMEOUT_MS, retryWindowMs: SERVER_DOCS_RETRY_WINDOW_MS, slowReportMs: SERVER_DOCS_SLOW_REPORT_MS, ...(budget ?? {}) };
}

/**
 * Waits between the tries of a server read that answers with a TRANSIENT error: 1 try + one retry per entry, so 3 tries by
 * default. A cold or restarting instance often answers unavailable / deadline-exceeded once and then serves; falling back to
 * the 10.7 MB direct read on the first such answer pays the heavy read for a server that was a second from ready. Bounded
 * by SERVER_DOCS_RETRY_WINDOW_MS: a retry whose wait would end past the window is not made.
 */
export const SERVER_DOCS_RETRY_DEFAULT_DELAYS_MS: readonly number[] = [500, 1_500];
// Under jest the waits start at zero, so a suite that makes the callable fail does not pay 2 s per read by accident.
let serverDocsRetryDelaysMs: readonly number[] = process.env.NODE_ENV === 'test' ? [0, 0] : SERVER_DOCS_RETRY_DEFAULT_DELAYS_MS;
/** Test hook: shorten (or zero) the retry waits. */
export function __setServerDocsRetryDelaysForTests(delays: readonly number[]): void {
  serverDocsRetryDelaysMs = delays;
}

/** Codes that mean "the server was not ready or hiccupped", not "you may not" or "your request is wrong". Uncoded errors are not retried. */
const TRANSIENT_SERVER_CODES: ReadonlySet<string> = new Set([
  'functions/unavailable',
  'functions/deadline-exceeded',
  'functions/internal',
]);

/** Run the server read, retrying (with the configured backoff) only a transient failure that came back inside the retry window. */
async function readServerWithRetry(
  readServer: (timeoutMs: number) => Promise<QueryDocumentSnapshot<DocumentData>[]>,
  startedAt: number,
  onAttempt: (at: number) => void
): Promise<QueryDocumentSnapshot<DocumentData>[]> {
  const delays = serverDocsRetryDelaysMs;
  for (let attempt = 0; ; attempt += 1) {
    const at = Date.now();
    onAttempt(at);
    try {
      // Each try gets only what is left of the budget, so a retry's call never outlives the client bound (a call that
      // did would keep downloading on the same weak link after the direct read started, and count against the rate limit).
      return await readServer(Math.max(1, serverDocsBudget.timeoutMs - (at - startedAt)));
    } catch (err) {
      const code = String((err as { code?: unknown })?.code ?? '');
      const inWindow = attempt < delays.length && Date.now() - startedAt + delays[attempt] <= serverDocsBudget.retryWindowMs;
      if (!TRANSIENT_SERVER_CODES.has(code) || !inWindow) throw err;
      await new Promise<void>((resolve) => setTimeout(resolve, delays[attempt]));
    }
  }
}

/**
 * Read through the server and WAIT for it, up to the timeout; read directly only if it fails (or times out). There is
 * deliberately no timed race: the direct read is 10.7 MB on the same phone link that is already slow for the ~290 KB
 * server answer, so starting it early only competes with an answer that is on its way (see SERVER_DOCS_TIMEOUT_MS).
 * @param {string} sourceId the server registry id (for reporting)
 * @param {Function} readServer the server read, already mapped to documents
 * @param {Function} readDirect the direct Firestore read, already mapped to documents
 * @return {Promise<QueryDocumentSnapshot<DocumentData>[]>} the documents from the server, or from the direct read
 */
async function readServerThenDirect(
  sourceId: string,
  readServer: (timeoutMs: number) => Promise<QueryDocumentSnapshot<DocumentData>[]>,
  readDirect: () => Promise<QueryDocumentSnapshot<DocumentData>[]>
): Promise<QueryDocumentSnapshot<DocumentData>[]> {
  // A phone that knows it is offline: the server cannot answer, so do not spend the retry backoff before the direct read.
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return readDirect();
  // The server timed out recently on this page: the link is dead or nearly so. Do not make every reopen wait the full
  // bound again; read directly until the cool-down ends (the offline copy, where there is one, is already on screen).
  const timedOutAt = serverDocsTimedOutAt.get(sourceId);
  if (timedOutAt !== undefined && Date.now() - timedOutAt < SERVER_DOCS_TIMEOUT_COOLDOWN_MS) {
    // Not reported to Sentry (one event per source per page load already exists); the console line makes it visible in a session.
    console.warn(`[dataSource] "${sourceId}" read directly: the server timed out recently (cool-down)`);
    return readDirect();
  }
  const startedAt = Date.now();
  let lastAttemptAt = startedAt;
  const { timeoutMs, slowReportMs } = serverDocsBudget;
  let timer: ReturnType<typeof setTimeout> | undefined;
  const bound = new Promise<never>((_, reject) => {
    timer = setTimeout(
      () => reject(Object.assign(new Error('server read exceeded the client timeout'), { code: CLIENT_TIMEOUT_CODE })),
      timeoutMs
    );
  });
  let docs: QueryDocumentSnapshot<DocumentData>[];
  try {
    docs = await Promise.race([readServerWithRetry(readServer, startedAt, (at) => { lastAttemptAt = at; }), bound]);
  } catch (err) {
    const code = String((err as { code?: unknown })?.code ?? '');
    // Arm the cool-down only after a LONG wait: the client bound, or the SDK's own deadline on a try that really ran
    // slowly. A QUICK deadline-exceeded is a cold instance (the case the retry window exists for), so it must not send
    // every reopen to the 10.7 MB direct read for 5 minutes. Judged per TRY (link health), unlike the slow report below, which
    // counts the person's whole wait.
    const slowTry = Date.now() - lastAttemptAt >= slowReportMs;
    if (code === CLIENT_TIMEOUT_CODE || (code === 'functions/deadline-exceeded' && slowTry)) serverDocsTimedOutAt.set(sourceId, Date.now());
    reportServerDocsFallback(sourceId, err, Date.now() - startedAt);
    return readDirect();
  } finally {
    if (timer !== undefined) clearTimeout(timer);
  }
  const now = Date.now();
  serverDocsTimedOutAt.delete(sourceId);
  // Judged and bucketed on the TOTAL wait from the first try: that is what the person waited, and generators-load-health counts
  // this marker (a read that retried and waited past the threshold in total was invisible when only the last try was timed). The
  // successful try's own time goes along as lastTryMs: long = a weak link, short after a long total = a cold server.
  const elapsedMs = now - startedAt;
  if (elapsedMs >= slowReportMs) reportServerDocsSlow(sourceId, elapsedMs, now - lastAttemptAt);
  return docs;
}

/** Sources the server refused for this session (permission-denied / unauthenticated): not asked again, one failed call is enough. */
const serverDocsRefused = new Set<string>();
/** When the server read last timed out, by source: inside SERVER_DOCS_TIMEOUT_COOLDOWN_MS reopens read directly at once. */
const serverDocsTimedOutAt = new Map<string, number>();
/** How long after a timeout reopens skip the server (a dead link should cost the 45 s wait once, not on every open). */
export const SERVER_DOCS_TIMEOUT_COOLDOWN_MS = 5 * 60 * 1000;
/** The code the client's OWN bound reports, so Sentry can tell it from the SDK's deadline-exceeded. */
export const CLIENT_TIMEOUT_CODE = 'client-timeout';
/** Fallbacks already reported to Sentry this page load, by source + code (one message each, not one per open). */
const serverDocsReported = new Set<string>();

/** Test hook: forget what this session learned. */
export function __resetServerDocsStateForTests(): void {
  serverDocsRefused.clear();
  serverDocsTimedOutAt.clear();
  __setServerDocsBudgetForTests(null);
  serverDocsReported.clear();
  serverDocsCopies.clear();
  serverDocsCopyStore = null;
  storeOps = Promise.resolve();
}

/** Test hook: resolves when every queued store write has run. */
export const __settleServerDocsStoreForTests = (): Promise<void> => storeOps;

/** What the browser says about the phone's link right now (Network Information API: Chrome/Android only; Safari has none). */
interface LinkQuality {
  effectiveType?: string;
  rtt?: number;
  downlink?: number;
  saveData?: boolean;
}

/**
 * The link quality to attach to a fallback report. A "slow" fallback is either a weak phone link (the answer was on its way)
 * or a cold server (the answer had not started): without this the two cannot be told apart, and they have opposite fixes
 * (client wait vs a warm server instance, which costs money). 'unknown' means the browser does not say, not "a good link".
 * @return {{netType: string, link: LinkQuality}} the tag value and the numbers for the event
 */
export function readLinkQuality(): { netType: string; link: LinkQuality } {
  const connection = typeof navigator === 'undefined' ?
    undefined :
    (navigator as Navigator & { connection?: LinkQuality }).connection;
  if (!connection) return { netType: 'unknown', link: {} };
  const { effectiveType, rtt, downlink, saveData } = connection;
  // A tag must stay low-cardinality: only the four values the spec defines, anything else is 'unknown'.
  const netType = typeof effectiveType === 'string' && /^(slow-2g|2g|3g|4g)$/.test(effectiveType) ? effectiveType : 'unknown';
  const link: LinkQuality = {};
  if (netType !== 'unknown') link.effectiveType = netType;
  if (typeof rtt === 'number') link.rtt = rtt;
  if (typeof downlink === 'number') link.downlink = downlink;
  if (typeof saveData === 'boolean') link.saveData = saveData;
  return { netType, link };
}

/**
 * Say that the server read fell back, where someone can see it: a console line on the phone alone would hide a
 * persistent server fault behind a working (but 10x heavier) direct read.
 * @param {string} sourceId the server registry id
 * @param {unknown} err what went wrong
 * @param {number} elapsedMs how long the server read ran before it failed
 */
function reportServerDocsFallback(sourceId: string, err: unknown, elapsedMs: number): void {
  const code = String((err as { code?: unknown })?.code ?? 'unknown');
  if (code === 'functions/permission-denied' || code === 'functions/unauthenticated') serverDocsRefused.add(sourceId);
  console.warn(`[useDataSource] server read of "${sourceId}" failed (${code}), reading Firestore directly`, err);
  const key = `${sourceId}:${code}`;
  if (serverDocsReported.has(key)) return;
  serverDocsReported.add(key);
  try {
    const { netType, link } = readLinkQuality();
    Sentry.captureMessage(`data source "${sourceId}" fell back to a direct Firestore read`, {
      level: 'warning',
      tags: { dataSource: sourceId, code, netType },
      extra: { message: err instanceof Error ? err.message : String(err), timeoutMs: serverDocsBudget.timeoutMs, elapsedMs, link },
    });
  } catch {
    /* reporting must never break the dropdown */
  }
}

/**
 * Say that the server read SUCCEEDED but slowly, with how long it took: the phone-side timing the fallback report alone
 * cannot give (the server's own log shows only its ~2 s, not the link). A different message from the fallback, so
 * "fell back" counts only real fallbacks. The elapsed tag is bounded to three buckets.
 * @param {string} sourceId the server registry id
 * @param {number} elapsedMs the whole wait from the first try, including any failed tries and backoff (what the tag buckets)
 * @param {number} lastTryMs how long the successful try alone took, end to end on the phone
 */
function reportServerDocsSlow(sourceId: string, elapsedMs: number, lastTryMs: number): void {
  const key = `${sourceId}:answered-slowly`;
  if (serverDocsReported.has(key)) return;
  serverDocsReported.add(key);
  try {
    const { netType, link } = readLinkQuality();
    const elapsed = elapsedMs < 20_000 ? '10-20s' : elapsedMs < 30_000 ? '20-30s' : '30-45s';
    Sentry.captureMessage(`data source "${sourceId}" answered slowly`, {
      level: 'info',
      tags: { dataSource: sourceId, netType, elapsed },
      extra: { elapsedMs, lastTryMs, link },
    });
  } catch {
    /* reporting must never break the dropdown */
  }
}

/**
 * Every document field the mapping below reads for this config, so a server answer that lacks one is refused
 * (and the direct read used) instead of quietly producing labels, autofill or sorting without it.
 * @param {DataSourceConfig} config the resolved config
 * @return {string[]} field names
 */
export function fieldsNeededFromDocs(config: DataSourceConfig): string[] {
  const need = new Set<string>(config.includeFields ?? []);
  const placeholder = /\{([^}]+)\}/g;
  let m: RegExpExecArray | null;
  const template = config.labelTemplate ?? '';
  while ((m = placeholder.exec(template)) !== null) need.add(m[1].trim());
  if (config.labelField) need.add(config.labelField);
  if (config.valueField && config.valueField !== 'id') need.add(config.valueField);
  if (config.sortField) need.add(config.sortField);
  if (config.computeLabel || (config.linkedFilters ?? []).some((f) => f.computeField)) COMPOSITE_INPUT_FIELDS.forEach((f) => need.add(f));
  return Array.from(need);
}

/**
 * Documents from the server's column-projected read, shaped like the Firestore snapshots the mapping code reads
 * (`id`, `data()`), in document-id order (what a Firestore scan returns, which de-dup and sort ties rely on). A null
 * cell means the field is absent. Throws on anything that is not a complete answer (wrong shape, no rows, a column
 * the config needs missing), so a malformed, empty or old-server response falls back to the direct read instead of
 * producing an empty or partial dropdown.
 * @param {string} sourceId the server registry id
 * @param {string} organizationId the caller's organization
 * @param {readonly string[]} required fields the mapping needs from each document
 * @param {number} timeoutMs the callable's timeout for this try (what is left of the budget)
 * @return {Promise<QueryDocumentSnapshot<DocumentData>[]>} snapshot-like documents
 */
export async function fetchServerDocs(
  sourceId: string,
  organizationId: string,
  required: readonly string[] = [],
  timeoutMs: number = serverDocsBudget.timeoutMs
): Promise<QueryDocumentSnapshot<DocumentData>[]> {
  const startedAt = Date.now();
  type Req = { sourceId: string; organizationId: string; acceptGzip?: boolean; since?: ReturnType<typeof sinceOf> };
  // Each call gets only what is LEFT of this try's budget (store wait included), so none can outlive the client bound and keep downloading on
  // the weak link after the direct read has started.
  const callWithin = () => httpsCallable<Req, ProjectedDocsResponse>(functions, 'forms_getDataSourceOptions', {
    timeout: Math.max(1, timeoutMs - (Date.now() - startedAt)),
  });
  const key = copyKey(sourceId, organizationId);
  const uid = currentUid();
  dropCopiesOfOtherUsers(uid);
  // Ask for the gzip answer only when this browser can inflate it. The plain answer is still accepted below, so an
  // older server (hosting and functions deploy hours apart) or a source that does not compress works unchanged.
  const base = { sourceId, organizationId, ...(canInflateGzip() ? { acceptGzip: true } : {}) };
  // No await unless the persistent store has to be asked: the call is issued in the same turn as before this change when memory answers.
  // A read with no uid yet (auth not restored) neither uses nor keeps a copy: it could not say whose it is.
  let candidate = uid === '' ? undefined : serverDocsCopies.get(memoryKey(key, uid));
  if (!candidate && uid !== '' && serverDocsCopyStore) {
    const stored = await loadStoredServerDocsCopy(serverDocsCopyStore, key);
    // A stored copy is trusted only after the checks a full answer gets (a partial write or an old shape must read as no copy); an unsound one is removed.
    candidate = validateCopy(stored);
    if (stored !== undefined && candidate === undefined) rememberServerDocsCopy(key, uid, undefined);
    // Kept in memory too, so a transient retry of this read does not pay the store wait again.
    if (candidate) serverDocsCopies.set(memoryKey(key, uid), candidate);
  }
  const held = usableCopy(candidate, required);
  let { data } = await callWithin()(held ? { ...base, since: sinceOf(held) } : base);

  if (data?.projectedDelta !== undefined) {
    // A delta is only ever answered to a request that carried `since`.
    const outcome = held ? applyDelta(held, data.projectedDelta) : ({ kind: 'malformed' } as const);
    if (outcome.kind === 'ok') {
      // Validated (rows, columns the mapping needs, not empty) BEFORE the copy is kept, so an unusable merge is never remembered or stored.
      const docs = docsFromRows(sourceId, outcome.copy.columns, outcome.copy.rows, required);
      rememberServerDocsCopy(key, uid, outcome.copy);
      return docs;
    }
    // The delta does not fit the copy (ids or count differ: a hard delete or a missed change; other columns; a malformed delta, or a delta
    // answered to a request with no `since`): drop the copy and ask once for the whole list. That request carries no `since`, so it can
    // never be answered with a delta again; if it is, the read fails below like any unusable answer. No loop.
    rememberServerDocsCopy(key, uid, undefined);
    reportServerDocsDeltaMismatch(sourceId, outcome.kind);
    ({ data } = await callWithin()(base));
  }
  // A gzip answer that cannot be inflated throws like any malformed answer: the caller falls back to the direct read.
  const projected = typeof data?.projectedDocsGz === 'string'
    ? (await inflateGzipBase64(data.projectedDocsGz) as ProjectedDocsResponse['projectedDocs'])
    : data?.projectedDocs;
  if (!projected || !Array.isArray(projected.columns) || projected.columns[0] !== 'id' || !Array.isArray(projected.rows)) {
    // An older server (functions leg not yet deployed) answers with the legacy options: hand them to a caller that can use them, so the
    // list is not downloaded a second time. The generators caller treats this like any failure (direct read).
    if (Array.isArray(data?.options) && data.options.length > 0) throw new LegacyAnswerError(data.options);
    throw new Error(`forms_getDataSourceOptions("${sourceId}") returned no projected documents`);
  }
  const docs = docsFromRows(sourceId, projected.columns, projected.rows, required);
  // Keep this answer as the base for the next read only when its snapshot proves it is the server's list (count and id fingerprint). An older
  // server sends no snapshot: nothing to keep, and nothing to clear unless a copy was held (no store traffic on every read until the server catches up).
  const next = copyFromFull(projected.columns, projected.rows, data.snapshot);
  if (next || held) rememberServerDocsCopy(key, uid, next);
  return docs;
}

/**
 * Turn projected rows into snapshot-like documents, refusing the same malformed shapes a full answer always has been refused for
 * (missing column the mapping needs, no rows, a row of the wrong width or without a string id), sorted by id.
 */
function docsFromRows(
  sourceId: string,
  columns: readonly string[],
  rows: readonly ProjectedRow[],
  required: readonly string[]
): QueryDocumentSnapshot<DocumentData>[] {
  const missing = required.filter((f) => !columns.includes(f));
  if (missing.length > 0) {
    throw new Error(`forms_getDataSourceOptions("${sourceId}") columns lack: ${missing.join(', ')}`);
  }
  if (rows.length === 0) {
    throw new Error(`forms_getDataSourceOptions("${sourceId}") returned no rows`);
  }
  const docs = rows.map((row) => {
    if (!Array.isArray(row) || row.length !== columns.length || typeof row[0] !== 'string') {
      throw new Error(`forms_getDataSourceOptions("${sourceId}") returned a malformed row`);
    }
    const fields: Record<string, unknown> = {};
    for (let i = 1; i < columns.length; i += 1) {
      if (row[i] !== null) fields[columns[i]] = row[i];
    }
    return { id: row[0] as string, data: () => fields } as unknown as QueryDocumentSnapshot<DocumentData>;
  });
  return docs.sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
}

/**
 * A delta that did not fit the copy, reported once per source per page load as a Sentry message (queryable and countable, unlike a breadcrumb, which
 * only rides on a later event). The read itself carries on with the full list; `reason` says whether the ids/columns differed ('mismatch') or the delta
 * was unusable ('malformed').
 */
function reportServerDocsDeltaMismatch(sourceId: string, reason: 'mismatch' | 'malformed'): void {
  const key = `${sourceId}:delta-${reason}`;
  if (serverDocsReported.has(key)) return;
  serverDocsReported.add(key);
  try {
    Sentry.captureMessage(`data source "${sourceId}" delta did not fit the copy; asked for the full list`, {
      level: 'info',
      tags: { dataSource: sourceId, reason },
    });
  } catch {
    /* reporting must never break the dropdown */
  }
}

// ============================================================================
// Firestore Query Builder (for authenticated forms)
// ============================================================================

export async function fetchDataSourceOptions(
  config: DataSourceConfig,
  formValues: Record<string, unknown>,
  organizationId?: string
): Promise<DataSourceOption[]> {
  if (!config.collection) {
    throw new Error('Data source config missing collection');
  }

  // Determine collection path
  let collectionPath = config.collection;

  // Handle org-scoped collections
  const orgScopedCollections = ['customers', 'users', 'branches', 'formTemplates'];
  if (orgScopedCollections.includes(config.collection) && organizationId) {
    collectionPath = `organizations/${organizationId}/${config.collection}`;
  }

  // Separate linked filters into Firestore-side and client-side
  const firestoreLinkedFilters: LinkedFilter[] = [];
  const clientSideLinkedFilters: LinkedFilter[] = [];
  if (config.linkedFilters) {
    for (const filter of config.linkedFilters) {
      if (filter.computeField) {
        // Client-side: uses a compute function instead of direct Firestore field
        clientSideLinkedFilters.push(filter);
      } else {
        firestoreLinkedFilters.push(filter);
      }
    }
  }

  // Build query constraints
  const constraints: QueryConstraint[] = [];

  // Add static filters
  if (config.filters) {
    for (const filter of config.filters) {
      constraints.push(where(filter.field, filter.operator, filter.value));
    }
  }

  // Add Firestore-side linked filters
  for (const filter of firestoreLinkedFilters) {
    const sourceValue = formValues[filter.sourceField];
    if (sourceValue !== undefined && sourceValue !== null && sourceValue !== '') {
      constraints.push(where(filter.field, filter.operator, sourceValue));
    }
  }

  // Add org filter for global collections that have organizationId
  const globalCollectionsWithOrg = ['assets', 'appsheet_onpoint_users', 'fleet_inspections', 'fleet_defects'];
  if (globalCollectionsWithOrg.includes(config.collection) && organizationId) {
    const orgField = config.collection === 'assets' ? 'orgId' : 'organizationId';
    constraints.push(where(orgField, '==', organizationId));
  }

  // NOTE: Sorting is done in-memory after fetching to avoid Firestore composite
  // index requirements. Equality filters + orderBy on different fields need
  // composite indexes that may not exist, causing queries to return 0 results.

  const collectionRef = collection(db, collectionPath);

  const applyPostFilters = (docs: QueryDocumentSnapshot<DocumentData>[]) => {
    // AppSheet-backed sources: drop in-place soft-deleted rows from dropdowns.
    let result = config.collection?.startsWith('appsheet_')
      ? docs.filter(doc => !isSoftDeleted(doc.data()))
      : docs;
    // Post-query filtering for fields that can't be filtered in Firestore
    // (e.g., Stage is stored as string but needs numeric comparison)
    if (config.collection === 'appsheet_service_logs') {
      result = result.filter(doc => {
        const stage = Number(doc.data()['Stage']);
        return isNaN(stage) || stage < 7;
      });
    }
    return result;
  };

  let filteredDocs: QueryDocumentSnapshot<DocumentData>[];

  if (config.limit) {
    // PAGINATION (PR #2277 pattern): `config.limit` is an admin-configured,
    // optional cap — not a hardcoded safety bound — so the product intent
    // is "show me at most N matching options," not "show me an arbitrary N
    // docs before filtering." A bare limit(config.limit) here truncates
    // BEFORE the Stage/linked-filter/dedupe filters run, silently dropping
    // valid dropdown options as the underlying collection grows. We instead
    // page through the collection (ordered by document ID) applying all
    // in-memory filters per page, stopping once we have enough filtered
    // results or the collection is exhausted.
    const PAGE_SIZE = Math.max(config.limit * 2, 100);
    const MAX_PAGES = 50;
    const accumulated: QueryDocumentSnapshot<DocumentData>[] = [];
    let cursor: QueryDocumentSnapshot<DocumentData> | null = null;
    let hitCap = true;

    for (let page = 0; page < MAX_PAGES; page += 1) {
      const pageQuery: Query<DocumentData> = cursor
        ? query(collectionRef, ...constraints, orderBy(documentId()), startAfter(cursor), limit(PAGE_SIZE))
        : query(collectionRef, ...constraints, orderBy(documentId()), limit(PAGE_SIZE));

      const snap = await getDocs(pageQuery);
      if (snap.empty) {
        hitCap = false;
        break;
      }
      accumulated.push(...applyPostFilters(snap.docs));
      if (snap.docs.length < PAGE_SIZE) {
        hitCap = false;
        break;
      }
      if (accumulated.length >= config.limit) {
        hitCap = false;
        break;
      }
      cursor = snap.docs[snap.docs.length - 1];
    }

    if (hitCap) {
      // eslint-disable-next-line no-console
      console.error(
        `useDataSource: pagination hit MAX_PAGES (${MAX_PAGES}) for collection ` +
          `"${config.collection}"; dropdown options may be truncated at ${accumulated.length} docs.`,
      );
    }

    filteredDocs = accumulated.slice(0, config.limit);
  } else {
    // No admin-configured limit: fetch the full matching set (unchanged
    // behavior — this is the common case for all built-in presets).
    const q = constraints.length > 0 ? query(collectionRef, ...constraints) : query(collectionRef);
    const readDirect = async () => applyPostFilters((await getDocs(q)).docs);
    if (config.serverDocs && constraints.length === 0 && organizationId && !serverDocsRefused.has(config.serverDocs)) {
      // The server sends the same documents column-projected (~1 MB instead of 10.7 MB for generators); the
      // mapping, filters, order and de-dup below are unchanged. ANY failure (an external user the callable's gate
      // refuses, a server not yet deployed with the source, offline, an empty or short answer, columns that lack a field
      // this config needs) falls back to the direct read; a SLOW answer is waited for (up to SERVER_DOCS_TIMEOUT_MS), not raced.
      filteredDocs = await readServerThenDirect(
        config.serverDocs,
        (timeoutMs) => fetchServerDocs(config.serverDocs as string, organizationId, fieldsNeededFromDocs(config), timeoutMs).then(applyPostFilters),
        readDirect
      );
    } else {
      filteredDocs = await readDirect();
    }
  }

  return mapDocsToOptions(config, filteredDocs, formValues);
}

/**
 * The options a config makes of documents (Firestore snapshots or the server's projected rows shaped like them): client-side linked
 * filters, label/value/data mapping, in-memory sort and de-duplication. One mapping for the direct read, the serverDocs read and the
 * serverFetch+serverDocs read, so the three can never drift.
 * @param {DataSourceConfig} config the resolved config
 * @param {QueryDocumentSnapshot[]} filteredDocs the documents, already post-filtered
 * @param {Record<string, unknown>} formValues the form's values (client-side linked filters)
 * @return {DataSourceOption[]} the options
 */
function mapDocsToOptions(
  config: DataSourceConfig,
  docs: QueryDocumentSnapshot<DocumentData>[],
  formValues: Record<string, unknown>
): DataSourceOption[] {
  const clientSideLinkedFilters = (config.linkedFilters ?? []).filter((f) => f.computeField);
  let filteredDocs = docs;
  // Apply client-side linked filters (using compute functions)
  for (const filter of clientSideLinkedFilters) {
    const sourceValue = formValues[filter.sourceField];
    if (sourceValue === undefined || sourceValue === null || sourceValue === '') continue;
    const computeFn = COMPUTE_LABEL_FUNCTIONS[filter.computeField!];
    if (!computeFn) continue;
    filteredDocs = filteredDocs.filter(doc => {
      const computedValue = computeFn(doc.data());
      return computedValue === String(sourceValue);
    });
  }

  // Resolve computeLabel function
  const computeLabelFn = config.computeLabel
    ? COMPUTE_LABEL_FUNCTIONS[config.computeLabel]
    : undefined;

  // Map documents to options
  const options: DataSourceOption[] = [];

  for (const doc of filteredDocs) {
    const data = doc.data();

    // Generate label (computeLabel > labelTemplate > labelField > doc.id)
    let label: string;
    if (computeLabelFn) {
      label = computeLabelFn(data);
    } else if (config.labelTemplate) {
      label = formatLabelTemplate(config.labelTemplate, data);
    } else if (config.labelField) {
      label = String(data[config.labelField] || doc.id);
    } else {
      label = doc.id;
    }

    // Get value (computeLabelAsValue uses computed label as value)
    let value: string;
    if (config.computeLabelAsValue && computeLabelFn) {
      value = label;
    } else if (config.valueField === 'id') {
      value = doc.id;
    } else if (config.valueField) {
      const vf = config.valueField;
      const resolved = vf.includes('.')
        ? resolveDotPath(data, vf)
        : data[vf];
      value = String(resolved || doc.id);
    } else {
      value = doc.id;
    }

    // Include additional fields
    const optionData: Record<string, unknown> = { id: doc.id };
    if (config.includeFields) {
      for (const field of config.includeFields) {
        const value = field.includes('.')
          ? resolveDotPath(data, field)
          : data[field];
        if (value !== undefined) {
          optionData[field] = value;
        }
      }
    }

    // Add computed fields to option data for auto-fill backfill
    // e.g., _compositeClientName so equipment selection can backfill customer
    if (config.collection === 'appsheet_generators') {
      const compositeFn = COMPUTE_LABEL_FUNCTIONS['compositeClientName'];
      if (compositeFn) {
        optionData['_compositeClientName'] = compositeFn(data);
      }
    }

    options.push({ label, value, data: optionData });
  }

  // Sort in memory (avoids Firestore composite index requirements)
  if (config.sortField) {
    const dir = config.sortDirection === 'desc' ? -1 : 1;
    const sf = config.sortField;
    options.sort((a, b) => {
      const resolveField = (d: Record<string, unknown> | undefined) => {
        if (!d) return '';
        return sf.includes('.')
          ? String(resolveDotPath(d, sf) ?? '')
          : String(d[sf] ?? '');
      };
      const aVal = resolveField(a.data as Record<string, unknown>) || a.label;
      const bVal = resolveField(b.data as Record<string, unknown>) || b.label;
      return dir * aVal.localeCompare(bVal);
    });
  }

  // Deduplicate by value if configured (e.g., unique client names from service logs)
  if (config.deduplicate) {
    const seen = new Set<string>();
    return options.filter(opt => {
      if (seen.has(opt.value)) return false;
      seen.add(opt.value);
      return true;
    });
  }

  return options;
}

// ============================================================================
// Helpers
// ============================================================================

/**
 * Resolve a dot-separated field path on a nested object.
 * e.g., resolveDotPath({ fields: { vin: "123" } }, "fields.vin") => "123"
 */
function resolveDotPath(data: Record<string, unknown>, path: string): unknown {
  let current: unknown = data;
  for (const key of path.split('.')) {
    if (current == null || typeof current !== 'object') return undefined;
    current = (current as Record<string, unknown>)[key];
  }
  return current;
}

/**
 * Format a label template with field values
 * Template: "{Make} {Model} ({Asset ID})"
 * Data: { Make: "Ford", Model: "F-150", "Asset ID": "V-001" }
 * Result: "Ford F-150 (V-001)"
 *
 * Pipe-delimited segments whose fields are all blank are dropped along with
 * their separator, so an optional field never leaves "A |  | C" behind
 * (ISS-431: 39% of generators have no sub-account). The template — never a
 * substituted value — is what gets split, so a "|" inside a client name cannot
 * fabricate a segment boundary.
 *
 * Mirrored server-side in functions/src/forms/getPublicDataSourceOptions.ts —
 * keep the two in step.
 */
export function formatLabelTemplate(template: string, data: Record<string, unknown>): string {
  return template
    .split('|')
    .map((segment) => substituteFields(segment, data).replace(/\s+/g, ' ').trim())
    .filter((segment) => segment !== '')
    .join(' | ');
}

function substituteFields(segment: string, data: Record<string, unknown>): string {
  return segment.replace(/\{([^}]+)\}/g, (match, field) => {
    const key = field.trim();
    const value = key.includes('.') ? resolveDotPath(data, key) : data[key];
    if (value === undefined || value === null) return '';
    return String(value);
  });
}
